/*
  Finish switch
  -------------
  Colour is a separate product per finish, each with its own prices, so
  switching finish means switching product. This swaps the product section —
  and the breadcrumb, which names the finish — for the target product's own
  rendered sections, then updates the URL and the page title. Price,
  compare-at, savings badge, SKU, gallery, dimension pills and the add-to-cart
  form therefore all come from the product actually on screen, and the form
  always posts a variant id belonging to it.

  Anything unexpected falls through to a normal page load, which produces the
  same result more slowly.
*/
class FinishSwatches extends HTMLElement {
  constructor() {
    super();
    this.loading = false;
    this.onClick = this.onClick.bind(this);
    this.onHistoryChange = this.onHistoryChange.bind(this);
  }

  connectedCallback() {
    if (this.dataset.ajax !== 'true' || !this.sectionElement()) return;

    this.addEventListener('click', this.onClick);
    window.addEventListener('popstate', this.onHistoryChange);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    window.removeEventListener('popstate', this.onHistoryChange);
  }

  sectionId() {
    return this.dataset.sectionId;
  }

  sectionElement() {
    return document.getElementById(`shopify-section-${this.sectionId()}`);
  }

  breadcrumbTarget() {
    const section = document.querySelector('.breadcrumb-trail')?.closest('[id^="shopify-section-"]');
    if (!section) return null;
    return { element: section, sectionId: section.id.replace('shopify-section-', '') };
  }

  onClick(event) {
    const link = event.target.closest('a[data-finish-link]');
    if (!link || link.classList.contains('is-current')) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
    if (this.loading) return;

    event.preventDefault();
    this.switchTo(link.href, true);
  }

  onHistoryChange(event) {
    if (!event.state?.finishUrl) return;
    this.switchTo(event.state.finishUrl, false);
  }

  switchTo(href, pushState) {
    const target = new URL(href, window.location.origin);
    const productSection = this.sectionElement();
    if (!productSection) {
      window.location.href = href;
      return;
    }

    const targets = [{ element: productSection, sectionId: this.sectionId() }];
    const breadcrumb = this.breadcrumbTarget();
    if (breadcrumb) targets.push(breadcrumb);

    this.loading = true;
    this.classList.add('is-loading');

    Promise.all(targets.map((entry) => this.fetchSection(target, entry.sectionId)))
      .then((markups) => {
        const parsed = markups.map((markup, index) =>
          new DOMParser().parseFromString(markup, 'text/html').getElementById(targets[index].element.id)
        );

        // The gallery is a slick carousel: it has to be torn down before its
        // markup is replaced and rebuilt after, with the theme's own settings.
        // Custom elements inside the section (variant-radios, product-form,
        // quantity-input, this element) upgrade by themselves.
        this.destroyGalleries(productSection);

        targets.forEach((entry, index) => {
          const source = parsed[index];
          if (source) entry.element.innerHTML = source.innerHTML;
        });

        this.buildGalleries(productSection);

        const heading = productSection.querySelector('h1');
        if (heading) document.title = heading.textContent.trim();

        if (pushState) history.pushState({ finishUrl: target.href }, '', target.href);

        document.dispatchEvent(new CustomEvent('finish:switched', { detail: { url: target.href } }));
      })
      .catch((error) => {
        console.error('Finish switch:', error);
        window.location.href = href;
      })
      .finally(() => {
        this.loading = false;
        this.classList.remove('is-loading');
      });
  }

  fetchSection(target, sectionId) {
    const params = new URLSearchParams(target.search);
    params.set('section_id', sectionId);

    return fetch(`${target.pathname}?${params.toString()}`).then((response) => {
      if (!response.ok) throw new Error(`${response.status} for ${target.pathname}`);
      return response.text();
    });
  }

  destroyGalleries(section) {
    if (typeof window.jQuery !== 'function') return;

    ['.product__media-list', '.product__media-sublist'].forEach((selector) => {
      const $slider = window.jQuery(section).find(selector);
      if ($slider.length && $slider.hasClass('slick-initialized')) $slider.slick('unslick');
    });
  }

  buildGalleries(section) {
    if (typeof window.jQuery !== 'function') return;

    const $ = window.jQuery;
    if (typeof window.getSliderSettings === 'function') {
      $(section).find('.product__media-list').slick(window.getSliderSettings());
    }
    if (typeof window.getSubSliderProductSettings === 'function') {
      $(section).find('.product__media-sublist').slick(window.getSubSliderProductSettings());
    }
  }
}

customElements.define('finish-swatches', FinishSwatches);
